import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AxiosErrorHandler, handleAxiosError } from 'common/error-handlers';
import { Form, Modal, Section, Row, Col, ModalFormActions, CheckboxInput, Skeleton, message, Title } from 'ui';

import { useUpdateNotificationSettings, useNotificationSettingsQuery } from '../../queries';

export type CommunityNotificationSettings = {
  emailNotificationsSettings: {
    newsfeed?: boolean;
    events?: boolean;
    messages?: boolean;
    programs?: boolean;
    donations?: boolean;
    files?: boolean;
    dailyDigestEvening?: boolean;
    dailyDigestMorning?: boolean;
  };
  pushNotificationsSettings: {
    newsfeed?: boolean;
    events?: boolean;
    messages?: boolean;
    programs?: boolean;
    donations?: boolean;
    files?: boolean;
  };
};

type P = {
  communityId: string;
  isOpened: boolean;
  onClose: () => void;
};

const NotificationSettingsModal = ({ communityId, onClose, ...rest }: P) => {
  const { t } = useTranslation();

  const {
    isLoading: isLoadingNotifications,
    isRefetching: isRefetchingNotifications,
    data: notificationSettings,
    error: queryError,
  } = useNotificationSettingsQuery({ id: communityId });

  const {
    isLoading: isUpdating,
    mutate: updateNotificationSettings,
    isSuccess: isUpdated,
    error: updateError,
  } = useUpdateNotificationSettings();

  useEffect(() => {
    if (isUpdated) {
      message.success('Notification settings was successfully updated.');
      onClose();
    }
  }, [onClose, isUpdated]);

  useEffect(() => {
    handleAxiosError(updateError);
  }, [updateError]);

  const initialValues: { values?: CommunityNotificationSettings; disables?: CommunityNotificationSettings } =
    useMemo(() => {
      if (notificationSettings) {
        return {
          values: {
            emailNotificationsSettings: {
              newsfeed: notificationSettings.emailNotificationsSettings.newsfeed.value,
              events: notificationSettings.emailNotificationsSettings.events.value,
              messages: notificationSettings.emailNotificationsSettings.messaging.value,
              programs: notificationSettings.emailNotificationsSettings.program.value,
              donations: notificationSettings.emailNotificationsSettings.donations.value,
              files: notificationSettings.emailNotificationsSettings.knowledgeCenter.value,
              dailyDigestEvening: notificationSettings.emailNotificationsSettings.dailyDigestEvening.value,
              dailyDigestMorning: notificationSettings.emailNotificationsSettings.dailyDigestMorning.value,
            },
            pushNotificationsSettings: {
              newsfeed: notificationSettings.pushNotificationsSettings.newsfeed.value,
              events: notificationSettings.pushNotificationsSettings.events.value,
              messages: notificationSettings.pushNotificationsSettings.messaging.value,
              programs: notificationSettings.pushNotificationsSettings.program.value,
              donations: notificationSettings.pushNotificationsSettings.donations.value,
              files: notificationSettings.pushNotificationsSettings.knowledgeCenter.value,
            },
          },
          disables: {
            emailNotificationsSettings: {
              newsfeed: !notificationSettings.emailNotificationsSettings.newsfeed.changeAllowed,
              events: !notificationSettings.emailNotificationsSettings.events.changeAllowed,
              messages: !notificationSettings.emailNotificationsSettings.messaging.changeAllowed,
              programs: !notificationSettings.emailNotificationsSettings.program.changeAllowed,
              donations: !notificationSettings.emailNotificationsSettings.donations.changeAllowed,
              files: !notificationSettings.emailNotificationsSettings.knowledgeCenter.changeAllowed,
              dailyDigestEvening: !notificationSettings.emailNotificationsSettings.dailyDigestEvening.changeAllowed,
              dailyDigestMorning: !notificationSettings.emailNotificationsSettings.dailyDigestMorning.changeAllowed,
            },
            pushNotificationsSettings: {
              newsfeed: !notificationSettings.pushNotificationsSettings.newsfeed.changeAllowed,
              events: !notificationSettings.pushNotificationsSettings.events.changeAllowed,
              messages: !notificationSettings.pushNotificationsSettings.messaging.changeAllowed,
              programs: !notificationSettings.pushNotificationsSettings.program.changeAllowed,
              donations: !notificationSettings.pushNotificationsSettings.donations.changeAllowed,
              files: !notificationSettings.pushNotificationsSettings.knowledgeCenter.changeAllowed,
            },
          },
        };
      }

      return {};
    }, [notificationSettings]);

  const handleSubmit = async (values: CommunityNotificationSettings) => {
    updateNotificationSettings({ id: communityId, values });
  };

  return (
    <Modal
      title={t('Community Settings')}
      disableBack
      onCancel={onClose}
      closable={!isUpdating}
      {...rest}
      destroyOnClose
    >
      <AxiosErrorHandler error={queryError} actions={false}>
        {isLoadingNotifications || isRefetchingNotifications ? (
          <Skeleton active paragraph={{ rows: 5 }} />
        ) : (
          <Form<CommunityNotificationSettings>
            initialValues={initialValues.values}
            onFinish={handleSubmit}
            className="community-notification-settings"
          >
            <Section paddingBottom paddingTop={false}>
              <Row>
                <Col item={{ span: 12 }}>
                  <Title item={{ level: 5 }}>Email Notifications</Title>
                  <CheckboxInput
                    item={{
                      name: ['emailNotificationsSettings', 'newsfeed'],
                      label: 'Newsfeed',
                      valuePropName: 'checked',
                    }}
                    input={{ disabled: initialValues.disables?.emailNotificationsSettings.newsfeed }}
                  />
                  <CheckboxInput
                    item={{
                      name: ['emailNotificationsSettings', 'events'],
                      label: 'Events',
                      valuePropName: 'checked',
                    }}
                    input={{ disabled: initialValues.disables?.emailNotificationsSettings.events }}
                  />
                  <CheckboxInput
                    item={{
                      name: ['emailNotificationsSettings', 'messages'],
                      label: 'Messages',
                      valuePropName: 'checked',
                    }}
                    input={{ disabled: initialValues.disables?.emailNotificationsSettings.messages }}
                  />
                  <CheckboxInput
                    item={{
                      name: ['emailNotificationsSettings', 'programs'],
                      label: 'Programs',
                      valuePropName: 'checked',
                    }}
                    input={{ disabled: initialValues.disables?.emailNotificationsSettings.programs }}
                  />
                  <CheckboxInput
                    item={{
                      name: ['emailNotificationsSettings', 'donations'],
                      label: 'Donations',
                      valuePropName: 'checked',
                    }}
                    input={{ disabled: initialValues.disables?.emailNotificationsSettings.donations }}
                  />
                  <CheckboxInput
                    item={{ name: ['emailNotificationsSettings', 'files'], label: 'Files', valuePropName: 'checked' }}
                    input={{ disabled: initialValues.disables?.emailNotificationsSettings.files }}
                  />
                  <CheckboxInput
                    item={{
                      name: ['emailNotificationsSettings', 'dailyDigestEvening'],
                      label: 'Daily Digest (Evening time)',
                      valuePropName: 'checked',
                    }}
                    input={{ disabled: initialValues.disables?.emailNotificationsSettings.dailyDigestEvening }}
                  />
                  <CheckboxInput
                    item={{
                      name: ['emailNotificationsSettings', 'dailyDigestMorning'],
                      label: 'Daily Digest (Morning time)',
                      valuePropName: 'checked',
                    }}
                    input={{ disabled: initialValues.disables?.emailNotificationsSettings.dailyDigestMorning }}
                  />
                </Col>
                <Col item={{ span: 12 }}>
                  <Title item={{ level: 5 }}>Push Notifications</Title>
                  <CheckboxInput
                    item={{
                      name: ['pushNotificationsSettings', 'newsfeed'],
                      label: 'Newsfeed',
                      valuePropName: 'checked',
                    }}
                    input={{ disabled: initialValues.disables?.pushNotificationsSettings.newsfeed }}
                  />
                  <CheckboxInput
                    item={{
                      name: ['pushNotificationsSettings', 'events'],
                      label: 'Events',
                      valuePropName: 'checked',
                    }}
                    input={{ disabled: initialValues.disables?.pushNotificationsSettings.events }}
                  />
                  <CheckboxInput
                    item={{
                      name: ['pushNotificationsSettings', 'messages'],
                      label: 'Messages',
                      valuePropName: 'checked',
                    }}
                    input={{ disabled: initialValues.disables?.pushNotificationsSettings.messages }}
                  />
                  <CheckboxInput
                    item={{
                      name: ['pushNotificationsSettings', 'programs'],
                      label: 'Programs',
                      valuePropName: 'checked',
                    }}
                    input={{ disabled: initialValues.disables?.pushNotificationsSettings.programs }}
                  />
                  <CheckboxInput
                    item={{
                      name: ['pushNotificationsSettings', 'donations'],
                      label: 'Donations',
                      valuePropName: 'checked',
                    }}
                    input={{ disabled: initialValues.disables?.pushNotificationsSettings.donations }}
                  />
                  <CheckboxInput
                    item={{ name: ['pushNotificationsSettings', 'files'], label: 'Files', valuePropName: 'checked' }}
                    input={{ disabled: initialValues.disables?.pushNotificationsSettings.files }}
                  />
                </Col>
              </Row>
            </Section>
            <ModalFormActions
              right={{ onClick: onClose, children: 'Cancel', disabled: isUpdating }}
              submit={{ children: 'Save', loading: isUpdating }}
            />
          </Form>
        )}
      </AxiosErrorHandler>
    </Modal>
  );
};

export default NotificationSettingsModal;
